'use client';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { throttle } from 'throttle-debounce';
import { makeStyles } from 'tss-react/mui';

import MenuIcon from '../../icons/Menu';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import CustomNavigationBar from '../CustomNavigationBar';
import IconMenuButton from '../IconMenuButton';
import NotificationsMenu from '../NotificationsMenu';
import ProfileMenu from '../ProfileMenu';
import Search from '../Search';
import Toolbar from '../Toolbar';
import ApolloWaves from './ApolloWaves';
import NavigationBarLogo from './NavigationBarLogo';
import NavigationMenuItem from './NavigationMenuItem';

const wavesLeftSvg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="381" height="57" viewBox="0 0 381 57" fill="none">
    <g opacity="0.48" filter="url(#filter0_f_8925_41189)">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M178.357 0.330078C52.3497 5.91657 20.2232 38.0444 -17.3403 38.0444C-54.9037 38.0444 -93.9329 0.330078 -121 0.330078C-121 0.330078 131.9 0.330078 178.357 0.330078Z" fill="#9E54B0"/>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M261.643 0.330078C143.432 6.15278 120.977 52.1872 75.9356 49.8301C30.8939 47.4729 14.5575 0.330078 -33 0.330078C-33 0.330078 218.06 0.330078 261.643 0.330078Z" fill="#0557D5"/>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M332.071 0.424128C230.545 54.0164 106.922 23.8146 61 0.424128H332.071Z" fill="#CC0077"/>
    </g>
    <defs>
      <filter id="filter0_f_8925_41189" x="-169" y="-47.6699" width="549.071" height="145.587" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
        <feGaussianBlur stdDeviation="24" result="effect1_foregroundBlur_8925_41189"/>
      </filter>
    </defs>
  </svg>`;

const wavesRightSvg = `
  <svg width="631" height="56" viewBox="0 0 631 56" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.06" d="M173 32C239.093 32 258.5 0 343.484 0H7.01061C74.1053 0 106.907 32 173 32Z" fill="white"/>
    <path opacity="0.24" fill-rule="evenodd" clip-rule="evenodd" d="M899 0C788 0 787.5 56 702 56C670.079 56 438.135 56 341.999 56C245.863 56 228.609 0 98.9998 0C-30.6097 0 869.711 0 899 0Z" fill="#0D0D0D"/>
  </svg>`;

const wavesClassicSvg = `
  <svg width="112" height="56" viewBox="0 0 112 56" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g id="background-waves" clip-path="url(#clip0_8472_542136)">
      <path id="purple rectangle" opacity="0.56" fill-rule="evenodd" clip-rule="evenodd" d="M-18 0C4.57037 0 47.9262 0 112.067 0C97.3558 11.4489 81 14.7822 63 10C37.9099 3.33413 25.1638 -1.30978 -0.00466919 4.36824C-10.1289 6.65226 -18 0 -18 0Z" fill="#9E54B0"/>
      <path id="pink Oval" opacity="0.48" fill-rule="evenodd" clip-rule="evenodd" d="M90 3.8147e-06C46.9281 22.7361 -5.51773 9.92323 -25 3.8147e-06H90Z" fill="#DF216D"/>
      <path id="blue rectangle" opacity="0.48" fill-rule="evenodd" clip-rule="evenodd" d="M73 0C22.8499 2.47024 13.3237 22 -5.7849 21C-24.8935 20 -31.8241 0 -52 0C-52 0 54.5103 0 73 0Z" fill="#0768FD"/>
      <path id="green rectangle" opacity="0.4" fill-rule="evenodd" clip-rule="evenodd" d="M54.5996 0C1.1419 2.37003 -12.4875 16 -28.4235 16C-44.3595 16 -60.9174 0 -72.4004 0C-72.4004 0 34.8905 0 54.5996 0Z" fill="#00C221"/>
    </g>
    <defs>
      <clipPath id="clip0_8472_542136">
      <rect width="112" height="56" fill="white"/>
      </clipPath>
    </defs>
  </svg>
`;

const wavesLeft = `data:image/svg+xml;base64,${btoa(wavesLeftSvg)}`;
const wavesRight = `data:image/svg+xml;base64,${btoa(wavesRightSvg)}`;
const wavesClassic = `data:image/svg+xml;base64,${btoa(wavesClassicSvg)}`;

export const styles = (
  { idsTokens = idsTokensDefault },
  { variant = 'classic', maxWidth = '100%' }
) => ({
  root: {
    padding: 0,
    background:
      variant === 'classic'
        ? 'linear-gradient(90deg, #250056 4.5%, #0076AE 100%)'
        : `linear-gradient(90deg, rgba(0, 0, 0, 0.72) 7.7%, rgba(0, 0, 0, 0.16) 100%), ${idsTokens.navbar[variant].color.surface.background.enabled}`,
    /**
     * Increment z-index when the page contains a loader, id we don't the Loader will
     * be render4ed over the NavigationBar, this was implemented this way to avoid
     * decreasing the z-index of the loader because that causes it to be rendered
     * under other elements it needs to cover
     */
    'html:has([data-loader-inner="true"]) &, html:has([data-progress-indicator-inner="true"]) &': {
      zIndex: 3004,
    },
  },
  toolbar: {
    height: idsTokens.navbar[variant].sizing.item.height,
    width: maxWidth,
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    '&.MuiToolbar-root': {
      padding: 0,
      paddingLeft: 16,
    },
    '@media (min-width:600px)': {
      minHeight: 56,
    },
  },
  container: {
    display: 'flex',
    wrap: 'nowrap',
    height: idsTokens.navbar[variant].sizing.item.height,
    alignItems: 'center',
    justifyContent: 'end',
  },
  title: {
    paddingTop: 16,
    paddingRight: 24,
    paddingBottom: 16,
    color: idsTokens.navbar[variant].color.content.text.enabled,
    '& .MuiTypography-root': {
      color: idsTokens.navbar[variant].color.content.text.enabled,
    },
  },
  navItems: {
    flexGrow: 1,
  },
  utility: {
    paddingRight: 8,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },

  waves: {
    position: 'absolute',
    left: -16,
  },
  menuIcon: {
    borderRadius: 0,
    padding: `
      ${idsTokens.navbar[variant].spacing.item.top}
      ${idsTokens.navbar[variant].spacing.item.right}
      ${idsTokens.navbar[variant].spacing.item.bottom}
      ${idsTokens.navbar[variant].spacing.item.left}
    `,
    width: 52,
    height: 56,
    color: 'currentColor',
    '& svg': {
      width: 20,
      height: 24,
    },
    '&.Mui-focusVisible': {
      outlineOffset: -2,
    },
    '&:hover': {
      backgroundColor: idsTokens.navbar[variant].color.surface.item.hover,
      color: idsTokens.navbar[variant].color.content.item.hover,
      '& svg': {
        color: idsTokens.navbar[variant].color.content.item.hover,
      },
    },
    '&:active': {
      backgroundColor: idsTokens.navbar[variant].color.surface.item.pressed,
      color: idsTokens.navbar[variant].color.content.item.pressed,
      '& svg': {
        color: idsTokens.navbar[variant].color.content.item.pressed,
      },
    },
    '&[aria-expanded="true"]': {
      backgroundColor: idsTokens.navbar[variant].color.surface.item.selected,
      color: idsTokens.navbar[variant].color.content.item.selected,
      '& svg': {
        color: idsTokens.navbar[variant].color.content.item.selected,
      },
      '&:hover': {
        backgroundColor: idsTokens.navbar[variant].color.surface.item.hover,
        color: idsTokens.navbar[variant].color.content.item.selected,
        '& svg': {
          color: idsTokens.navbar[variant].color.content.item.selected,
        },
      },
      '&:active': {
        backgroundColor: idsTokens.navbar[variant].color.surface.item.pressed,
        color: idsTokens.navbar[variant].color.content.item.selected,
        '& svg': {
          color: idsTokens.navbar[variant].color.content.item.selected,
        },
      },
    },
  },
  // Variants - Skins
  accent: {
    backgroundRepeat: 'no-repeat, no-repeat',
    '& .MuiBox-root:first-of-type': {
      backgroundColor: idsTokens.navbar.accent.color.surface.background.overlay,
    },
  },
  brand: {
    backgroundRepeat: 'no-repeat, no-repeat',
  },
  classic: {
    backgroundRepeat: 'no-repeat',
    '& .MuiBox-root:first-of-type': {
      backgroundColor: idsTokens.navbar.classic.color.surface.background.overlay,
    },
  },
  inverse: {
    backgroundRepeat: 'no-repeat, no-repeat',
    '& .MuiBox-root:first-of-type': {
      backgroundColor: idsTokens.navbar.inverse.color.surface.background.overlay,
    },
  },
  neutral: {
    backgroundRepeat: 'no-repeat, no-repeat',
    '& .MuiBox-root:first-of-type': {
      backgroundColor: idsTokens.navbar.neutral.color.surface.background.overlay,
    },
  },
  searchField: {
    minWidth: 50,
    marginTop: 12,
    flexGrow: 1,
    flexShrink: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginLeft: 'auto',
    marginRight: '16px',
    '& input': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
});

const useStyles = makeStyles()(styles);

const getWaves = (waveVariant, left, right) => {
  let waveString = '';
  switch (waveVariant) {
    case 'left':
      waveString = `url(${left}),`;
      break;
    case 'right':
      waveString = `url(${right}),`;
      break;
    case 'both':
      waveString = `url(${left}), url(${right}),`;
      break;
    case 'none':
      waveString = '';
      break;
    default:
      waveString = `url(${left}), url(${right}),`;
  }
  return waveString;
};

const getGradient = (
  noGradient,
  backgroundColor,
  firstColor,
  firstPercentage,
  secondColor,
  secondPercentage,
  defaultBackground
) => {
  let gradientString = '';
  if (backgroundColor == null && (noGradient == null || noGradient == false)) {
    return `linear-gradient(90deg, ${firstColor} ${firstPercentage}, ${secondColor} ${secondPercentage})`;
  }
  if (backgroundColor != null) {
    gradientString = backgroundColor;
  }
  if (backgroundColor == null && noGradient != null && noGradient == true) {
    gradientString = defaultBackground;
  }

  return gradientString;
};

const getBackground = (variant, waveVariant, noGradient, backgroundColor, idsTokens) => {
  let wave = '';
  let gradient = '';
  switch (variant) {
    case 'accent':
      wave = getWaves(waveVariant, wavesLeft, wavesRight);
      gradient = getGradient(
        noGradient,
        backgroundColor,
        idsTokens.navbar.accent.color.surface.background.gradient.stop1,
        '0%',
        idsTokens.navbar.accent.color.surface.background.gradient.stop2,
        '100%',
        idsTokens.navbar.accent.color.surface.background.enabled
      );
      break;
    case 'brand':
      wave = getWaves(waveVariant, wavesLeft, wavesRight);
      gradient = getGradient(
        noGradient,
        backgroundColor,
        idsTokens.navbar.brand.color.surface.background.gradient.stop1,
        '7.7%',
        idsTokens.navbar.brand.color.surface.background.gradient.stop2,
        '100%',
        idsTokens.navbar.brand.color.surface.background.enabled
      );
      break;
    case 'classic':
      wave = getWaves(waveVariant, wavesClassic);
      gradient = `linear-gradient(90deg, ${idsTokens.navbar.classic.color.surface.background.gradient.stop1} 4.5%, ${idsTokens.navbar.classic.color.surface.background.gradient.stop2} 100%), ${idsTokens.navbar.classic.color.surface.background.enabled}`;
      break;
    case 'inverse':
      wave = getWaves(waveVariant, wavesLeft, wavesRight);
      gradient = getGradient(
        noGradient,
        backgroundColor,
        idsTokens.navbar.inverse.color.surface.background.gradient.stop1,
        '7.7%',
        idsTokens.navbar.inverse.color.surface.background.gradient.stop2,
        '100%',
        idsTokens.navbar.inverse.color.surface.background.enabled
      );
      break;
    case 'neutral':
      wave = getWaves(waveVariant, wavesLeft, wavesRight);
      gradient = getGradient(
        noGradient,
        backgroundColor,
        idsTokens.navbar.neutral.color.surface.background.gradient.stop1,
        '7.7%',
        idsTokens.navbar.neutral.color.surface.background.gradient.stop2,
        '100%',
        idsTokens.navbar.neutral.color.surface.background.enabled
      );

      break;
    default:
      wave = `url(${wavesClassic})`;
      gradient = getGradient(
        noGradient,
        backgroundColor,
        idsTokens.navbar.accent.color.surface.background.gradient.stop1,
        '0%',
        idsTokens.navbar.accent.color.surface.background.gradient.stop2,
        '100%',
        idsTokens.navbar.accent.color.surface.background.enabled
      );
  }
  return wave + gradient;
};

const generateBackground = (
  waves,
  variant,
  waveVariant,
  noGradient,
  backgroundColor,
  idsTokens
) => {
  const backgroundPositionString =
    waveVariant === 'right' ? 'right top, right center' : 'left top, right top, right center';
  const oldBackgroundCSS =
    variant === 'classic'
      ? 'linear-gradient(90deg, #250056 4.5%, #0076AE 100%)'
      : `linear-gradient(90deg, rgba(0, 0, 0, 0.72) 7.7%, rgba(0, 0, 0, 0.16) 100%), ${idsTokens.navbar[variant].color.surface.background.enabled}, &:has(+ .MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPaper-elevation1 div div .MuiCircularProgress-root) : {
    zIndex: 3004 
  }`;
  const backgroundCSS = getBackground(variant, waveVariant, noGradient, backgroundColor, idsTokens);

  return waves
    ? { background: oldBackgroundCSS }
    : {
        background: backgroundCSS,
        backgroundSize: 'unset',
        backgroundPosition: backgroundPositionString,
      };
};

export const NavigationBar = ({
  logoProps,
  menuItems = [],
  notificationsMenuProps,
  profileMenuProps,
  LogoComponent,
  onClick,
  checkIsActive,
  maxWidth,
  waves,
  otherButtons,
  variant = 'classic',
  waveVariant,
  noGradient,
  backgroundColor,
  className,
  sx,
  groupingButtonLabel = 'More',
  truncate = false,
  showMenuIcon = false,
  showSearch = false,
  SearchProps,
  slots,
  slotProps,
  ...rest
}) => {
  const { classes, cx, theme } = useStyles({ maxWidth, variant });
  const idsTokens = theme?.idsTokens ?? idsTokensDefault;
  const backgroundStyle = generateBackground(
    waves,
    variant,
    waveVariant,
    noGradient,
    backgroundColor,
    idsTokens
  );
  const menuItemsCount = menuItems.length;
  const menuItemsLabels = menuItems.map((item) => item.text).join(',');
  const [widths, setWidths] = React.useState([]);
  const [overflowIndex, setOverflowIndex] = React.useState(menuItemsCount);
  const profileMenuRef = useRef();
  const logoRef = useRef();

  const selectRef = React.useRef();
  const [refs, setRefs] = React.useState(
    Array(menuItemsCount)
      .fill()
      .map(() => React.createRef())
  );

  React.useEffect(() => {
    setOverflowIndex(menuItemsCount);
    setRefs(
      Array(menuItemsCount)
        .fill()
        .map(() => React.createRef())
    );
  }, [menuItemsCount]);

  React.useEffect(() => {
    if (truncate) {
      setWidths(refs.map((ref) => ref.current?.getBoundingClientRect().width || 0));
    }
  }, [menuItemsCount, menuItemsLabels, refs, truncate]);

  const checkOverflow = React.useCallback(
    throttle(100, () => {
      if (widths.length) {
        const logoWidth = logoRef.current?.getBoundingClientRect().width ?? 0;
        const profileMenuWidth = profileMenuRef.current?.getBoundingClientRect().width ?? 0;
        const searchWidth = showSearch ? 96 : 0;
        const tabsWidth = window.innerWidth - logoWidth - profileMenuWidth - searchWidth - 96;
        // 96 is coming from padding left + on the toolbar + padding right on the logo + padding right on the profileMenu + 32 (padding left + padding right) to give them more room during resize calculation
        const selectWidth = selectRef.current?.getBoundingClientRect().width ?? 0;
        let width = 0;
        let newStart = -1;
        for (let i = 0; i < widths.length; i++) {
          width += widths[i];
          if (width > tabsWidth) {
            if (width - widths[i] + selectWidth > tabsWidth) {
              newStart = i - 1;
            } else {
              newStart = i;
            }
            break;
          }
        }
        if (width < tabsWidth) {
          newStart = widths.length;
        }
        if (newStart !== -1) {
          setOverflowIndex(newStart);
        }
      }
    }),
    [widths, showSearch]
  );

  const { ref: resizeRef } = useResizeDetector({ onResize: checkOverflow });

  return (
    <CustomNavigationBar
      ref={resizeRef}
      className={waves ? cx(classes.root, className) : cx(classes[variant], className)}
      sx={[waves ? undefined : backgroundStyle, ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    >
      <Toolbar className={classes.toolbar}>
        <div className={cx(classes.container, classes.title)} ref={logoRef}>
          {waves && <ApolloWaves variant={variant} className={classes.waves} />}
          {showMenuIcon && (
            <IconMenuButton
              id="actions"
              size="small"
              menuItems={menuItems}
              className={classes.menuIcon}
              darkMode={variant !== 'neutral'}
              minMenuWidth={260}
              placement="bottom-start"
              PopperMenuProps={{
                PaperProps: {
                  sx: {
                    width: 262,
                    boxSizing: 'border-box',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    '& .MuiMenuItem-root': {
                      padding: '8px 16px 8px 8px !important',
                      height: '40px !important',
                      boxSizing: 'border-box',
                      fontSize: `${idsTokens.typography.font.size.base} !important`,
                      lineHeight: `${idsTokens.typography.lineHeight.base} !important`,
                      fontWeight: `${idsTokens.typography.font.weight.regular} !important`,
                    },
                  },
                },
                modifiers: [
                  {
                    name: 'offset',
                    options: { offset: [0, 8] },
                  },
                ],
              }}
            >
              <MenuIcon />
            </IconMenuButton>
          )}
          {/*This icon will trigger the menuItems as dropdown list on xs an s breakpoints*/}
          {(LogoComponent && <LogoComponent />) ||
            (logoProps && <NavigationBarLogo {...logoProps} />)}
        </div>

        <div className={cx(classes.container, classes.navItems)}>
          {menuItems?.map(
            (navItem, i) =>
              i < overflowIndex && (
                <div ref={refs[i]} key={i}>
                  <NavigationMenuItem
                    variant={variant}
                    onClick={onClick && ((props) => onClick(props ?? navItem))}
                    active={checkIsActive?.(navItem)}
                    checkIsActive={checkIsActive}
                    slots={slots}
                    slotProps={slotProps}
                    {...navItem}
                  />
                </div>
              )
          )}
          {overflowIndex < menuItemsCount && (
            <div ref={selectRef}>
              <NavigationMenuItem
                variant={variant}
                checkIsActive={checkIsActive}
                key="more"
                onClick={onClick && ((props) => onClick(props))}
                text={groupingButtonLabel}
                menuItems={menuItems.slice(overflowIndex)}
                slots={slots}
                slotProps={slotProps}
              />
            </div>
          )}
        </div>

        <div className={cx(classes.container, classes.utility)}>
          {showSearch && (
            <Search {...SearchProps} className={cx(classes.searchField, SearchProps?.className)} />
          )}
          {otherButtons}
          {notificationsMenuProps && (
            <NotificationsMenu variant={variant} {...notificationsMenuProps} />
          )}
          {profileMenuProps && (
            <div ref={profileMenuRef}>
              <ProfileMenu
                variant={variant}
                onClick={onClick}
                active={checkIsActive?.(profileMenuProps)}
                checkIsActive={checkIsActive}
                {...profileMenuProps}
              />
            </div>
          )}
        </div>
      </Toolbar>
    </CustomNavigationBar>
  );
};

NavigationBar.propTypes = {
  /** Background color for the navbar. This disables the gradient automatically. */
  backgroundColor: PropTypes.string,
  /**
   * A function that determines which menu is styled as active.
   *
   * @param {object} item The menu item to test.
   */
  checkIsActive: PropTypes.func,
  /** Label for the button group grouping the MenuItems on smaller devices. */
  groupingButtonLabel: PropTypes.string,
  /** Custom component to display a logo. Used as an alternative to logoProps. */
  LogoComponent: PropTypes.elementType,
  /** Props applied to the logo component. */
  logoProps: PropTypes.shape({
    /** Alternative text for the image. */
    alt: PropTypes.string,
    /** Callback fired when the logo image is clicked. */
    onClick: PropTypes.func,
    /** Source of the logo image. */
    src: PropTypes.string,
  }),
  /** Maximum width of navigation bar content. */
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** The list of navigation items to be displayed in the component. */
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      /** If `true`, the component is displayed in an active state. */
      active: PropTypes.bool,
      /** URL of the page the link goes to. */
      href: PropTypes.string,
      /** The list of sub navigation items to be displayed in the component. */
      menuItems: PropTypes.arrayOf(
        PropTypes.shape({
          /** If `true`, the component is displayed in an active state. */
          active: PropTypes.bool,
          /** URL of the page the link goes to. */
          href: PropTypes.string,
          /**
           * If `true`, the menu item is a title.
           * This is used to visually separate groups of menu items.
           */
          isTitle: PropTypes.bool,
          /** Path of the menu item. Used in `checkIsActive`. */
          pathname: PropTypes.string,
          /** The content of the menu item. */
          text: PropTypes.node,
        })
      ),
      /** Path of the navigation item. Used in `checkIsActive`. */
      pathname: PropTypes.string,
      /** The content of the menu item. */
      text: PropTypes.node,
    })
  ),
  /** If `true`, the gradient will not show. */
  noGradient: PropTypes.bool,
  /** Props applied to the `NotificationMenu` component. */
  notificationsMenuProps: PropTypes.shape({
    /** The list of actions shown when the actions icon button is clicked. */
    actionsMenuItems: PropTypes.arrayOf(
      PropTypes.shape({
        /** Callback fired when the component is clicked. */
        onClick: PropTypes.func,
        /** The content of the menu item. */
        text: PropTypes.string,
      })
    ),
    /** Customize the text shown when there are no notifications. */
    emptyText: PropTypes.node,
    /** Customize the 'Notifications' header. */
    headerText: PropTypes.string,
    /** Customize the text of the 'Mark as read' action tooltip. */
    markReadText: PropTypes.string,
    /** Customize the text of the 'Mark as unread' action tooltip. */
    markUnreadText: PropTypes.string,
    /** Field that uniquely identifies each notification. `id` by default. */
    notificationIdField: PropTypes.string,
    /** The list of notifications. */
    notifications: PropTypes.arrayOf(
      PropTypes.shape({
        /** The message of the notification. */
        details: PropTypes.node,
        /** The title of the notification. */
        header: PropTypes.node,
        /** The icon of the notification. */
        icon: PropTypes.elementType,
        /** Unique ID of the notification. */
        id: PropTypes.any,
        /**
         * Callback fired when a notification is clicked.
         *
         * @param {any} id The id of the notification.
         */
        onClick: PropTypes.func,
        /**
         * Callback fired when the notification's delete button is clicked.
         *
         * @param {any} id The id of the notification.
         */
        onDelete: PropTypes.func,
        /**
         * Callback fired when the notification's read/unread button is clicked.
         *
         * @param {any} id The id of the notification.
         */
        onToggleRead: PropTypes.func,
        /** The time of the notification. */
        timestamp: PropTypes.object,
        /** If `true`, the notification is unread. */
        unread: PropTypes.bool,
      })
    ),
    /**
     * Callback fired when the NotificationsMenu button is clicked.
     */
    onButtonClick: PropTypes.func,
    /**
     * Callback fired when a notification is clicked.
     *
     * @param {any} id The id of the notification.
     */
    onClick: PropTypes.func,
    /**
     * Callback fired when the notification's delete button is clicked.
     *
     * @param {any} id The id of the notification.
     */
    onDelete: PropTypes.func,
    /**
     * Callback fired when the notification's read/unread button is clicked.
     *
     * @param {any} id The id of the notification.
     */
    onToggleRead: PropTypes.func,
    /** If `true`, the component is displayed in an active state. */
    open: PropTypes.bool,
    /** Customize the text of the 'Remove' action tooltip. */
    removeText: PropTypes.string,
  }),
  /**
   * Callback fired when a navigation item is clicked.
   *
   * @param {object} item The properties of the menu item that was clicked.
   */
  onClick: PropTypes.func,
  /** Additional top level navigational items to include. */
  otherButtons: PropTypes.node,
  /** Props applied to the `ProfileMenu` component. */
  profileMenuProps: PropTypes.shape({
    /** The email address of the active user. */
    email: PropTypes.node,
    /** Custom content shown at the end of the menu. */
    extraContent: PropTypes.node,
    /** If `true`, the logout button is hidden. */
    hideLogout: PropTypes.bool,
    /** Add an icon to the profile menu button. */
    icon: PropTypes.element,
    /** Props applied to the logout button. */
    logoutButtonProps: PropTypes.object,
    /** Text displayed in the logout button. */
    logoutText: PropTypes.node,
    /** The list of navigation items. */
    menuItems: PropTypes.arrayOf(
      PropTypes.shape({
        /** If `true`, the component is displayed in an active state. */
        active: PropTypes.bool,
        /** URL of the page the link goes to. */
        href: PropTypes.string,
        /** Add an icon to the menu item. */
        icon: PropTypes.elementType,
        /** Path of the menu item. Used in `checkIsActive`. */
        pathname: PropTypes.string,
        /** The content of the menu item. */
        text: PropTypes.node,
      })
    ),
    /** The name of the active user. */
    name: PropTypes.node,
    /** Props applied to the `Popper` component. */
    PopperProps: PropTypes.object,
    /** The business title of the active user. */
    title: PropTypes.node,
  }),
  /** Props applied to the `Search` component. Relevant when `showSearch` is `true`. */
  SearchProps: PropTypes.object,
  /** If `true`, the Menu Icon is rendered. */
  showMenuIcon: PropTypes.bool,
  /** If `true`, the search field is rendered. */
  showSearch: PropTypes.bool,
  /** The props used for each slot inside. */
  slotProps: PropTypes.shape({
    /**
     * Props applied to the `NavigationMenuItem` slot.
     */
    NavigationMenuItem: PropTypes.object,
  }),
  /** The components used for each slot inside. */
  slots: PropTypes.shape({
    /**
     * The element used as the Navigation Menu Item.
     */
    NavigationMenuItem: PropTypes.elementType,
  }),
  /** If `true`, the Menu Items will group under a "More" dropdown button when space is constrained. */
  truncate: PropTypes.bool,
  /** The variant to use. */
  variant: PropTypes.oneOf(['accent', 'brand', 'classic', 'inverse', 'neutral']),
  /** If `true`, the wave style is applied. */
  waves: PropTypes.bool,
  /** Wave variant to use. */
  waveVariant: PropTypes.oneOf(['left', 'right', 'both', 'none']),
};

export default NavigationBar;
